import { NodeModel } from "../../../models/node-model";
import { trimEnd, trimStart, last, findLast, size, trim } from "lodash";

export interface IStructureParser<T> {
	parseRawInput(rawInput: string): T;
	parseRawInputToImportVM(rawInput: string): any;
}

export abstract class NodeModelParserBase implements IStructureParser<NodeModel[]> {
	abstract parseRawInput(rawInput: string): NodeModel[];
	abstract parseRawInputToImportVM(rawInput: string): any;

	resolveParent(model: NodeModel, nodes: NodeModel[]): void {
		const comparer = last(nodes);

		if (comparer) {
			if (comparer.level < model.level) {
				model.parent = comparer;
				model.parentName = comparer.title;
			} else if (comparer.level === model.level) {
				model.parent = comparer.parent;
				model.parentName = comparer.parentName;
			} else {
				const sameLevel = findLast(nodes, (node: NodeModel) => node.level === model.level) ||
					{ parent: null, parentName: null, sortOrder: 1 };
				model.parent = model.level === 0 ? null : sameLevel.parent;
				model.parentName = model.level === 0 ? null : sameLevel.parentName;
			}
		}
	}

	resolveSortOrder(model: NodeModel, nodes: NodeModel[]): void {
		model.sortOrder = size(nodes.filter((node: NodeModel) => {
			return node.level === model.level && node.parent === model.parent;
		})) + 1;
	}

	createNode(nodeTitle: string, identifier: string, level: number, nodes: NodeModel[]): NodeModel {
		const model = new NodeModel(nodeTitle, 1, null, identifier.length > 0 ? identifier : null);

		model.level = level;

		this.resolveParent(model, nodes);
		this.resolveSortOrder(model, nodes);

		return model;
	}
}

export class OrganizationStructureParser extends NodeModelParserBase {
	private parseFlatNodes(row: string, nodes: NodeModel[]): void {
		if (row.length > 0) {
			if (row.trim() == "") { return; }

			const orgOrCcIndex = row.indexOf("\t");
			const orgOrCc = row.slice(0, orgOrCcIndex);
			row = trimStart(row.substring(orgOrCcIndex));

			let identifier: string;

			const identifierEndingIndex = row.indexOf("\t");
			if (identifierEndingIndex >= 0) {
				identifier = row.slice(0, identifierEndingIndex).trim();
				row = row.substring(identifierEndingIndex);
			}
			else {
				identifier = row;
				row = "";
			}

			const startOfName = row.search(/\S/);
			const whiteSpaceInStart = startOfName >= 0 ? row.substring(0, startOfName) : row;
			const tabCount = whiteSpaceInStart.split("\t").length - 1;
			const nodeTitle = row.trim();

			const model = this.createNode(`${identifier}: ${nodeTitle}`, orgOrCc, tabCount - 1, nodes);
			nodes.push(model);
		}
	}

	parseRawInput(rawInput: string): NodeModel[] {
		const nodes = new Array<NodeModel>();
		if (!rawInput) {
			return nodes;
		}

		const rows = rawInput.split('\n');
		rows.forEach(row => this.parseFlatNodes(row, nodes));

		return nodes;
	}

	parseRawInputToImportVM(rawInput): any {
		const nodes = this.parseRawInput(rawInput);
		return nodes.map(node => ({
			identifier: node.title.split(": ")[0],
			name: node.title.split(": ")[1],
			sortOrder: node.sortOrder,
			typeCode: node.id,
			parentName: node.parentName ? node.parentName.split(": ")[1] : null,
			level: node.level
		}));
	}
}

export class ServiceStructureParser extends NodeModelParserBase {

	private defaultMeasuresSeparator = "#";
	private extraFieldsSeparator = "&";

	private parseFlatNodes(row: string, nodes: NodeModel[]): void {
		if (row.length > 0) {
			row = trimEnd(row, "\t");

			const identifierIndex = row.indexOf("\t");
			const identifier = row.slice(0, identifierIndex);
			row = row.replace(identifier, "");

			const tabs = row.search(/\S/);
			row = trimStart(row, "\t");

			const titleIndex = row.indexOf("\t") !== -1 ? row.indexOf("\t") : row.length;
			const nodeTitle = row.slice(0, titleIndex);
			
			const model = this.createNode(nodeTitle, identifier, tabs - 1, nodes);

			// Parse and set data items and metadata for the node
			const metadata = this.parseRawMetadata(row);
			model.defaultDataItems = metadata.defaultDataItems;
			model.extraFields = metadata.extraFields;

			nodes.push(model);
		}
	}

	parseRawInput(rawInput: string): NodeModel[] {
		const nodes = new Array<NodeModel>();
		if (!rawInput) {
			return nodes;
		}

		const rows = rawInput.split('\n');
		rows.forEach(row => this.parseFlatNodes(row, nodes));

		return nodes;
	}

	parseRawMetadata(input: string): any {
		const hasDefaultDataItems = input.includes(this.defaultMeasuresSeparator);
		const hasExtraFields = input.includes(this.extraFieldsSeparator);

		let defaultDataItems: string[] = [];
		let extraFields: string[] = [];

		// Parse measures from input string
		if (hasDefaultDataItems) {
			const startIndex = input.indexOf(this.defaultMeasuresSeparator);
			const endIndex = hasExtraFields ? input.indexOf(this.extraFieldsSeparator) : input.length;
			const dataItemsString = trim(input.slice(startIndex + 1, endIndex), "\t");
			defaultDataItems = dataItemsString
				.split(",")
				.filter(m => m.length > 0)
				.map(m => m.trim());
		}

		if (hasExtraFields) {
			const startIndex = input.indexOf(this.extraFieldsSeparator);
			const endIndex = input.length;	
			const metaString = input.slice(startIndex + 2, endIndex); // +2 to skip the separator and following tab
			extraFields = metaString
				.split("\t")
				.map(ef => ef.trim());

			// Trim if there are only empty strings
			if (extraFields.every(f => f.length === 0)) {
				extraFields = [];
			}

			// Extra fields needs to be 4 elements long if there are any, and we need to preserve the order
			if (extraFields.length > 0) {
				const extraFieldsLength = 4;
				if (extraFields.length < extraFieldsLength) {
					const diff = extraFieldsLength - extraFields.length;
					for (let i = 0; i < diff; i++) {
						extraFields.push("");
					}
				}
			}

		}

		return {
			defaultDataItems,
			extraFields
		}
	}

	parseRawInputToImportVM(rawInput): any {
		const nodes = this.parseRawInput(rawInput);
		return nodes.map(node => this.mapNodeToImportVM(node));
	}

	private validateExtraFields(extraFields: string[] | undefined): boolean {
		return extraFields && extraFields.length === 4;
	}

	private mapNodeToImportVM(node: NodeModel): IServiceStructureServiceImportVM {
		return {
			title: node.title,
			sortOrder: node.sortOrder,
			code: node.id,
			parentName: node.parentName,
			parent: node.parent,
			level: node.level,
			defaultDataItems: node.defaultDataItems,
			auraIdentifier: this.validateExtraFields(node.extraFields) && node.extraFields[0] !== "" ? node.extraFields[0] : null,
			orpIdentifier: this.validateExtraFields(node.extraFields) && node.extraFields[1] !== "" ? node.extraFields[1] : null,
			extraField1: this.validateExtraFields(node.extraFields) && node.extraFields[2] !== "" ? node.extraFields[2] : null,
			extraField2: this.validateExtraFields(node.extraFields) && node.extraFields[3] !== "" ? node.extraFields[3] : null,
		};
	}
}

export interface IServiceStructureServiceImportVM {
	title: string;
	sortOrder: number;
	code: string;
	parentName: string;
	parent: NodeModel;
	level: number;
	defaultDataItems: string[];
	auraIdentifier: string;
	orpIdentifier: string;
	extraField1: string;
	extraField2: string;
}
